/**
 * EL ORDEN DENTRO DE UN DIA DE LA LINEA DE TIEMPO.
 *
 * EL FALLO QUE ESTO CORRIGE lo describio el propietario asi: «cuando escribo una
 * nota salen hasta abajo… se supone que esto es una linea de tiempo». Tenia
 * razon, y la causa era que el dia se armaba metiendo PRIMERO todas las
 * operaciones y DESPUES todas las notas, asi que una nota escrita a las 18:39
 * caia por debajo de una operacion abierta a las 22:05.
 *
 * QUE HORA REPRESENTA A UNA OPERACION. La apertura, y el plan si todavia no se
 * abrio. No es una eleccion de gusto: es la MISMA regla con la que se decidio en
 * que dia va la operacion (`trade_date_local` sale de la apertura, y del plan
 * mientras no la haya). Ordenar dentro del dia por un momento distinto del que
 * eligio el dia seria usar dos relojes para la misma cosa.
 *
 * El CIERRE no sirve para esto aunque parezca mas "cuando termino": una
 * operacion abierta a las 13:00 y cerrada a las 15:30 se iria al final del dia,
 * por debajo de las notas que escribiste mientras corria. Una publicacion se
 * ordena por cuando EMPEZO, como en cualquier otro feed.
 *
 * Funciones puras y en su propio archivo: `web/` no monta DOM en los tests
 * (SPEC 20), y un orden mal hecho no da ningun error — da una pantalla que
 * miente sobre lo que paso antes.
 */

/** Lo minimo que hace falta para ordenar: ni la tarjeta ni el resto del DTO. */
export type Orderable = {
  kind: "trade" | "note";
  id: string;
  /** Cuando se escribio, si es una nota. */
  postedAtUtc?: string | null | undefined;
  /** Los momentos de la operacion, si lo es. */
  openedAtUtc?: string | null | undefined;
  plannedAtUtc?: string | null | undefined;
};

/**
 * El instante por el que se ordena, en milisegundos, o `null` si no hay ninguno
 * legible.
 *
 * `null` y no cero: cero es 1970 y mandaria el elemento al final del dia como si
 * fuera lo mas viejo que paso. Lo que no tiene hora se trata aparte.
 */
export function instantOf(item: Orderable): number | null {
  const raw =
    item.kind === "note"
      ? item.postedAtUtc
      : (item.openedAtUtc ?? item.plannedAtUtc);

  if (raw === null || raw === undefined || raw === "") return null;

  const parsed = Date.parse(raw);

  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mas reciente primero, como el resto de la linea de tiempo.
 *
 * DOS DESEMPATES, y los dos hacen falta:
 *
 *   1. Lo que NO tiene hora legible va al final del dia, no al principio. Sin
 *      esto se colaria arriba del todo, que es el sitio mas visible de la
 *      pantalla, por no saber cuando paso.
 *   2. A igualdad de instante manda el `id`. Los identificadores son uuid v7 —
 *      ordenan por tiempo de creacion —, asi que dos cosas del mismo minuto
 *      quedan en el orden en que se escribieron. Y, sobre todo, el orden es
 *      ESTABLE: sin desempate, `sort` puede devolver un orden distinto en cada
 *      repintado y las tarjetas bailarian bajo el dedo.
 */
export function byNewestFirst(a: Orderable, b: Orderable): number {
  const left = instantOf(a);
  const right = instantOf(b);

  if (left === null && right === null) return b.id.localeCompare(a.id);
  if (left === null) return 1;
  if (right === null) return -1;

  return left === right ? b.id.localeCompare(a.id) : right - left;
}
